import { FC, Fragment, useEffect, useMemo, useState } from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import AddVoyageModal from './AddVoyageModal'
import AddLegModal from './AddLegModal'
import EditLegModal from './EditLegModal'
import { Voyage, Vessel, Leg } from '../core/_models'
import DeleteVoyageModal from './DeleteVoyageModal'
import EditVoyageModal from './EditVoyageModal'
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import StartEndVoyageModal from './StartEndVoyage';
import * as XLSX from 'xlsx';
import { endVoyage, getActiveVoyageForVessel, getVoyageList, startVoyage, startLeg, endLeg, deleteLeg } from '../core/_requests'
import { getApiErrorMessage } from '../../../shared/utils/apiErrorMessage'
import { OP } from '../core/operationPermissions'
import { useOperationsAccess } from '../core/useOperationsAccess'
import { computeOperationsScopeUx } from '../core/operationsScopeUx'
import { useAuth } from '../../auth'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { TableSkeleton } from '../../../shared/components/LoadingSkeletons'

const VoyageList: FC = () => {
    const { opCan, permissionsLoaded, isSuperAdmin } = useOperationsAccess()
    const { currentUser, isSuperAdmin: isAuthSuperAdmin, roleDetails } = useAuth()
    const scopeUx = useMemo(
        () => computeOperationsScopeUx(currentUser, isSuperAdmin),
        [currentUser, isSuperAdmin]
    )

    // Expanded voyage rows (for leg accordion)
    const [expandedVoyageIds, setExpandedVoyageIds] = useState<Set<number>>(new Set())

    // AddLegModal state
    const [addLegVoyage, setAddLegVoyage] = useState<Voyage | null>(null)

    // EditLegModal state
    const [editLegVoyageId, setEditLegVoyageId] = useState<number | null>(null)
    const [editLegTarget, setEditLegTarget] = useState<Leg | null>(null)

    const toggleExpanded = (voyageId: number) => {
        setExpandedVoyageIds(prev => {
            const next = new Set(prev)
            if (next.has(voyageId)) next.delete(voyageId)
            else next.add(voyageId)
            return next
        })
    }

    const handleLegAdded = (leg: Leg) => {
        // Refresh the voyage list to pick up the new leg
        fetchVoyages()
        setAddLegVoyage(null)
    }

    const handleLegUpdated = (leg: Leg) => {
        fetchVoyages()
        setEditLegVoyageId(null)
        setEditLegTarget(null)
    }

    const canDeleteVoyageForRow = (voyage: Voyage) => {
        if (!opCan(OP.DELETE_VOYAGE)) return false
        if (scopeUx.isCrew) {
            const isCurrentlyActive = Boolean(voyage.active)
            const hasFilledReports = (voyage.reportCount || 0) > 0
            if (isCurrentlyActive || hasFilledReports) return false
        }
        return true
    }
    const showActionsColumn = opCan(OP.UPDATE_VOYAGE) || opCan(OP.DELETE_VOYAGE)
    const [isAddVoyageModalOpen, setIsAddVoyageModalOpen] = useState(false)
    const [isEditVoyageModalOpen, setIsEditVoyageModalOpen] = useState(false)
    const [isDeleteVoyageModalOpen, setIsDeleteVoyageModalOpen] = useState(false)
    const [isStartEndVoyageModalOpen, setIsStartEndVoyageModalOpen] = useState(false)
    const [selectedVoyage, setSelectedVoyage] = useState<Voyage>()
    const [currentPage, setCurrentPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [searchTerm, setSearchTerm] = useState('')
    const [loading, setLoading] = useState(true)
    const [voyages, setVoyages] = useState<Voyage[]>([]);
    const [filteredVoyages, setFilteredVoyages] = useState<Voyage[]>([]);
    const [actionLoadingVoyageId, setActionLoadingVoyageId] = useState<number | null>(null);
    const [sortConfig, setSortConfig] = useState<{ key: keyof Voyage | null, direction: 'asc' | 'desc' }>({
        key: null,
        direction: 'asc',
    });
    const { state: globalState, vessels: globalVessels } = useGlobalFilter()

    // Sync global header search query → local searchTerm
    useEffect(() => {
        setSearchTerm(globalState.searchQuery || '')
    }, [globalState.searchQuery])

    const isVoyageRestartDisabled = (voyage: Voyage) => {
        if (!voyage) return false
        const status = String(voyage.status || '').trim().toLowerCase()
        return status === 'completed' || status === 'ended' || status === 'inactive'
    }

    const handleSort = (key: keyof Voyage) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    }

    const formatDate = (dateString: string) => {
        console.log(dateString);
        if (!dateString) return null;
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const datePart = date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        }).replace(/ /g, '-');

        const timePart = date.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });

        return `${datePart} ${timePart}`;
    };

    // Filter voyages by search term
    const handlePageChange = (page: number) => {
        if (page > 0 && page <= totalPages) {
            setCurrentPage(page);
        }
    };
    const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setRowsPerPage(parseInt(e.target.value));
        setCurrentPage(1); // reset to first page
    };

    useEffect(() => {
        fetchVoyages()
    }, [])

    useEffect(() => {
        if (voyages.length > 0 && searchTerm) {
            const hasMatch = voyages.some(v => v.voyageNumber?.toLowerCase().includes(searchTerm.toLowerCase()));
            if (!hasMatch) {
                setSearchTerm(''); // Clear search if it causes empty view
            }
        }
    }, [voyages]);


    // Compute total pages based on filtered voyages
    const totalPages = Math.ceil(filteredVoyages.length / rowsPerPage) || 1

    // Ensure currentPage is within bounds when filteredVoyages or rowsPerPage change
    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(1)
        }
    }, [filteredVoyages, rowsPerPage, totalPages])

    // Slice filtered voyages for current page
    const displayedVoyages = filteredVoyages.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    )

    const fetchVoyages = async () => {
        try {
            setLoading(true)
            const voyageList = await getVoyageList()
            setVoyages(voyageList)
        } catch (error) {
            console.error('Failed to fetch voyage list:', error)
            toast.error(getApiErrorMessage(error, 'Failed to fetch voyage list.'))
        } finally {
            setLoading(false)
        }
    }

    const handleVoyageStatusUpdated = (updatedVoyage: Voyage) => {
        setVoyages((prev) => prev.map((v) => (v.id === updatedVoyage.id ? { ...v, ...updatedVoyage } : v)));
        setSelectedVoyage((prev) => (prev && prev.id === updatedVoyage.id ? { ...prev, ...updatedVoyage } : prev));
    }

    const handleStartFromActions = async (voyage: Voyage) => {
        if (isVoyageRestartDisabled(voyage)) {
            toast.info(`Voyage ${voyage.voyageNumber} has ended and cannot be started again.`)
            return
        }

        try {
            setActionLoadingVoyageId(voyage.id)
            const activeVoyages = await getActiveVoyageForVessel(voyage.vessel.id)
            const currentActiveVoyage = Array.isArray(activeVoyages) ? activeVoyages[0] : null

            if (currentActiveVoyage && currentActiveVoyage.id !== voyage.id) {
                toast.warning(`Voyage ${currentActiveVoyage.voyageNumber} is already active for this vessel.`)
                return
            }

            const res = await startVoyage(voyage.id)
            const updated = {
                ...voyage,
                ...(res && res.id ? res : {}),
                active: true,
                status: res?.status || 'started',
            }
            handleVoyageStatusUpdated(updated)
            toast.success(`Voyage ${voyage.voyageNumber} started successfully.`)
        } catch (error: any) {
            console.error('Failed to start voyage:', error)
            toast.error(getApiErrorMessage(error, 'Failed to start voyage.'))
        } finally {
            setActionLoadingVoyageId(null)
        }
    }

    const handleEndFromActions = async (voyage: Voyage) => {
        try {
            setActionLoadingVoyageId(voyage.id)
            const res = await endVoyage(voyage.id)
            const updated = {
                ...voyage,
                ...(res && res.id ? res : {}),
                active: false,
                status: res?.status || 'completed',
            }
            handleVoyageStatusUpdated(updated)
            toast.success(`Voyage ${voyage.voyageNumber} ended successfully.`)
        } catch (error: any) {
            console.error('Failed to end voyage:', error)
            toast.error(getApiErrorMessage(error, 'Failed to end voyage.'))
        } finally {
            setActionLoadingVoyageId(null)
        }
    }

    const getVoyageStatusBadge = (voyage: Voyage) => {
        const isStarted = voyage.active;
        const isEnded = !isStarted;
        const label = isEnded ? 'Ended' : 'Started';
        const className = isEnded ? 'badge badge-light-danger' : 'badge badge-light-success';

        return <span className={`${className} ms-2`}>{label}</span>;
    }

    const handleExcelDownload = () => {
        const table = document.querySelector('.report-table table') as HTMLTableElement;
        if (!table) return;

        const workbook = XLSX.utils.table_to_book(table, { sheet: "Voyages" });
        XLSX.writeFile(workbook, "voyages.xlsx");
    };

    useEffect(() => {
        const normalizedSearchTerm = searchTerm.trim().toLowerCase()
        const allowedVesselIds = globalState.company
            ? new Set(globalVessels.map((v: any) => Number(v.id)))
            : null

        const nextFilteredVoyages = voyages.filter((voyage) => {
            const matchesSearch = (voyage?.voyageNumber || '').toLowerCase().includes(normalizedSearchTerm)

            // Global filter: vessel
            let matchesVessel = true
            if (globalState.vessel?.id != null) {
                matchesVessel = String(voyage?.vessel?.id) === String(globalState.vessel.id)
            }

            // Global filter: company
            let matchesCompany = true
            if (globalState.company && allowedVesselIds) {
                const vId = voyage?.vessel?.id
                if (vId != null && allowedVesselIds.has(Number(vId))) {
                    matchesCompany = true
                } else {
                    const v: any = voyage
                    const voyageCgId = v.companyGroupAdminId ?? v.companyGroupId ?? v.companyGroupAdmin?.id ?? v.vessel?.companyGroupAdminId ?? v.vessel?.companyGroupId ?? v.vessel?.companyGroupAdmin?.id ?? v.vessel?.cgaid?.id ?? v.vessel?.cgaid
                    const voyageCaId = v.companyAdminId ?? v.companyId ?? v.companyAdmin?.id ?? v.vessel?.companyAdminId ?? v.vessel?.companyId ?? v.vessel?.companyAdmin?.id
                    if (globalState.company.type === 'company_group') {
                        matchesCompany = voyageCgId != null && Number(voyageCgId) === globalState.company.id
                    } else {
                        matchesCompany = voyageCaId != null && Number(voyageCaId) === globalState.company.id
                    }
                }
            }

            return matchesSearch && matchesVessel && matchesCompany
        })
        let sortedVoyages = [...nextFilteredVoyages];

        if (sortConfig.key === 'vessel') {
            sortedVoyages.sort((a, b) => {
                const valA = (a.vessel?.fleet_name ?? '').toLowerCase();
                const valB = (b.vessel?.fleet_name ?? '').toLowerCase();

                return sortConfig.direction === 'asc'
                    ? valA.localeCompare(valB)
                    : valB.localeCompare(valA);
            });
        }

        if (sortConfig.key !== null) {
            sortedVoyages.sort((a, b) => {
                const valA = (a[sortConfig.key!] ?? '').toString().toLowerCase();
                const valB = (b[sortConfig.key!] ?? '').toString().toLowerCase();

                if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
                if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        setFilteredVoyages(sortedVoyages);
    }, [voyages, sortConfig, searchTerm, globalState.vessel, globalState.company]);

    if (permissionsLoaded && !opCan(OP.READ_VOYAGE)) {
        return (
            <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
                <div className='alert alert-warning m-5'>You do not have permission to view voyages.</div>
            </div>
        )
    }

    return (
        <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
            <div className='d-flex flex-column flex-column-fluid'>
                <div id='kt_app_content' className='app-content flex-column-fluid d-flex flex-column' style={{ flex: 1 }}>
                    <div className='card flex-column-fluid d-flex flex-column bg-white' style={{ flex: 1 }}>
                        {/* Header and Controls */}
                        <div className='d-flex gap-3 pe-5 border-top'>
                            <div className='py-3 ms-3' style={{ flex: 5 }}>
                                <h3 className='card-title fw-bold text-dark mb-5'>Voyages</h3>
                                <div className='d-flex justify-content-between mb-5'>
                                    <div className='d-flex align-items-center gap-2'>
                                        <input
                                            type='text'
                                            className='form-control cp_search_input'
                                            placeholder='Voyage Number'
                                            value={searchTerm}
                                            onChange={e => {
                                                setSearchTerm(e.target.value)
                                                setCurrentPage(1)
                                            }}
                                        />
                                    </div>
                                    <div className='d-flex align-items-center gap-4'>
                                        {opCan(OP.DOWNLOAD_VOYAGE as any) && (
                                            <button className="btn p-0 m-0" onClick={handleExcelDownload}>
                                                <KTSVG path='/media/icons/duotune/general/download.svg' className='svg-icon-2x' />
                                            </button>
                                        )}
                                        {opCan(OP.CREATE_VOYAGE) && (
                                            <button className='btn btn_primary' onClick={() => setIsAddVoyageModalOpen(true)}>
                                                <KTSVG path='/media/map/zoom-in.svg' className='svg-icon-2' /> Add Voyage
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {/* Table */}
                                <div className='report-table table-responsive'>
                                    <table className="table table-bordered align-middle">
                                        <thead className="table-header text-start">
                                            <tr>
                                                <th onClick={() => handleSort('voyageNumber')} className="cursor-pointer">
                                                    VOYAGE NUMBER
                                                    <KTSVG
                                                        path={`/media/map/sort-col-${sortConfig.key === 'voyageNumber'
                                                            ? sortConfig.direction === 'asc'
                                                                ? 'up-black'
                                                                : 'down-black'
                                                            : 'grey'
                                                            }.svg`}
                                                        className="svg-icon ms-2 custom-sort-icon"
                                                    />
                                                </th>

                                                <th onClick={() => handleSort('vessel')} className="cursor-pointer">
                                                    VESSEL
                                                    <KTSVG
                                                        path={`/media/map/sort-col-${sortConfig.key === 'vessel'
                                                            ? sortConfig.direction === 'asc'
                                                                ? 'up-black'
                                                                : 'down-black'
                                                            : 'grey'
                                                            }.svg`}
                                                        className="svg-icon ms-2 custom-sort-icon"
                                                    />
                                                </th>

                                                <th onClick={() => handleSort('departurePort')} className="cursor-pointer">
                                                    DEPARTURE PORT
                                                    <KTSVG
                                                        path={`/media/map/sort-col-${sortConfig.key === 'departurePort'
                                                            ? sortConfig.direction === 'asc'
                                                                ? 'up-black'
                                                                : 'down-black'
                                                            : 'grey'
                                                            }.svg`}
                                                        className="svg-icon ms-2 custom-sort-icon"
                                                    />
                                                </th>

                                                <th onClick={() => handleSort('arrivalPort')} className="cursor-pointer">
                                                    ARRIVAL PORT
                                                    <KTSVG
                                                        path={`/media/map/sort-col-${sortConfig.key === 'arrivalPort'
                                                            ? sortConfig.direction === 'asc'
                                                                ? 'up-black'
                                                                : 'down-black'
                                                            : 'grey'
                                                            }.svg`}
                                                        className="svg-icon ms-2 custom-sort-icon"
                                                    />
                                                </th>

                                                <th onClick={() => handleSort('startDate')} className="cursor-pointer">
                                                    DEPARTURE DATETIME
                                                    <KTSVG
                                                        path={`/media/map/sort-col-${sortConfig.key === 'startDate'
                                                            ? sortConfig.direction === 'asc'
                                                                ? 'up-black'
                                                                : 'down-black'
                                                            : 'grey'
                                                            }.svg`}
                                                        className="svg-icon ms-2 custom-sort-icon"
                                                    />
                                                </th>

                                                <th onClick={() => handleSort('endDate')} className="cursor-pointer">
                                                    ARRIVAL DATETIME
                                                    <KTSVG
                                                        path={`/media/map/sort-col-${sortConfig.key === 'endDate'
                                                            ? sortConfig.direction === 'asc'
                                                                ? 'up-black'
                                                                : 'down-black'
                                                            : 'grey'
                                                            }.svg`}
                                                        className="svg-icon ms-2 custom-sort-icon"
                                                    />
                                                </th>

                                                {showActionsColumn && <th>ACTIONS</th>}

                                            </tr>
                                        </thead>
                                        <tbody className="table-body text-start">
                                            {loading ? (
                                                <TableSkeleton rows={8} cols={showActionsColumn ? 7 : 6} />
                                            ) : displayedVoyages.length === 0 ? (
                                                <tr>
                                                    <td colSpan={showActionsColumn ? 7 : 6} className="text-center py-4 text-muted">
                                                        No voyages found
                                                    </td>
                                                </tr>
                                            ) : (
                                                displayedVoyages.map((voyage, idx) => (
                                                    <Fragment key={voyage.id ?? idx}>
                                                        <tr>
                                                            <td
                                                                className={opCan(OP.READ_VOYAGE) ? 'text-primary fw-bold cursor-pointer' : 'text-primary fw-bold'}
                                                                onClick={() => {
                                                                    if (opCan(OP.READ_VOYAGE)) {
                                                                        setSelectedVoyage(voyage)
                                                                        setIsStartEndVoyageModalOpen(true)
                                                                    }
                                                                }}
                                                            >
                                                                <span className='d-inline-flex align-items-center gap-2'>
                                                                    {/* Expand/Collapse toggle for legs */}
                                                                    <button
                                                                        className="btn btn-sm btn-icon btn-light-primary me-1"
                                                                        style={{ width: 24, height: 24, padding: 0 }}
                                                                        title={expandedVoyageIds.has(voyage.id) ? 'Collapse legs' : 'Expand legs'}
                                                                        onClick={(e) => { e.stopPropagation(); toggleExpanded(voyage.id) }}
                                                                    >
                                                                        <span style={{ fontSize: 12 }}>{expandedVoyageIds.has(voyage.id) ? '▲' : '▼'}</span>
                                                                    </button>
                                                                    <span>{voyage.voyageNumber}</span>
                                                                    {voyage.charteredVoyageNumber && (
                                                                        <span className="text-muted fs-8">({voyage.charteredVoyageNumber})</span>
                                                                    )}
                                                                    {getVoyageStatusBadge(voyage)}
                                                                    {/* Leg count badge */}
                                                                    {voyage.legs && voyage.legs.length > 0 && (
                                                                        <span className="badge badge-light-info ms-1" title="Number of legs">
                                                                            {voyage.legs.length} leg{voyage.legs.length !== 1 ? 's' : ''}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <VesselLandingLink
                                                                    vesselId={voyage.vessel.id ?? null}
                                                                    label={voyage.vessel.fleet_name}
                                                                    className='text-dark text-decoration-underline'
                                                                />
                                                            </td>
                                                            <td>{voyage.departurePort}</td>
                                                            <td>{voyage.arrivalPort ?? <span className="text-muted">—</span>}</td>
                                                            <td>{formatDate(voyage.startDate)}</td>
                                                            <td>{formatDate(voyage.endDate)}</td>
                                                            {showActionsColumn && (
                                                                <td>
                                                                    <div className='d-flex align-items-center gap-2'>
                                                                        {opCan(OP.UPDATE_VOYAGE) && (
                                                                            <>
                                                                                <button
                                                                                    className="btn btn-sm px-0"
                                                                                    onClick={() => {
                                                                                        setSelectedVoyage(voyage)
                                                                                        setIsEditVoyageModalOpen(true)
                                                                                    }}
                                                                                >
                                                                                    <KTSVG path="/media/map/edit-active.svg" />
                                                                                </button>
                                                                                <button
                                                                                    className={`btn btn-sm ${voyage.active ? 'btn_danger' : isVoyageRestartDisabled(voyage) ? 'btn-secondary' : 'btn_success'}`}
                                                                                    onClick={() => {
                                                                                        if (voyage.active) {
                                                                                            handleEndFromActions(voyage)
                                                                                        } else if (!isVoyageRestartDisabled(voyage)) {
                                                                                            handleStartFromActions(voyage)
                                                                                        }
                                                                                    }}
                                                                                    disabled={actionLoadingVoyageId === voyage.id || (!voyage.active && isVoyageRestartDisabled(voyage))}
                                                                                    title={!voyage.active && isVoyageRestartDisabled(voyage) ? 'Completed voyages cannot be started again' : ''}
                                                                                >
                                                                                    {actionLoadingVoyageId === voyage.id
                                                                                        ? 'Please wait...'
                                                                                        : voyage.active
                                                                                            ? 'End Voyage'
                                                                                            : isVoyageRestartDisabled(voyage)
                                                                                                ? 'Ended'
                                                                                                : 'Start Voyage'}
                                                                                </button>
                                                                                {/* Add Leg button */}
                                                                                <button
                                                                                    className="btn btn-sm btn-light-success"
                                                                                    title="Add new leg to this voyage"
                                                                                    onClick={() => setAddLegVoyage(voyage)}
                                                                                >
                                                                                    + Leg
                                                                                </button>
                                                                            </>
                                                                        )}
                                                                        {canDeleteVoyageForRow(voyage) && (
                                                                            <button
                                                                                className='btn btn-icon btn-active-light-danger w-30px h-30px'
                                                                                onClick={() => {
                                                                                    setSelectedVoyage(voyage)
                                                                                    setIsDeleteVoyageModalOpen(true)
                                                                                }}
                                                                            >
                                                                                <KTSVG path='/media/map/trash.svg' className='svg-icon-1' />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            )}
                                                        </tr>

                                                        {/* ── Leg accordion sub-table ─────────────────── */}
                                                        {expandedVoyageIds.has(voyage.id) && (
                                                            <tr key={`${voyage.id}-legs`}>
                                                                <td colSpan={showActionsColumn ? 7 : 6} style={{ background: '#f8f9fa', paddingLeft: 40 }}>
                                                                    {!voyage.legs || voyage.legs.length === 0 ? (
                                                                        <div className="text-muted py-2">No legs yet.</div>
                                                                    ) : (
                                                                        <table className="table table-sm table-bordered mb-0">
                                                                            <thead className="table-light">
                                                                                <tr>
                                                                                    <th>Seq</th>
                                                                                    <th>From</th>
                                                                                    <th>To</th>
                                                                                    <th>Departure</th>
                                                                                    <th>Arrival</th>
                                                                                    <th>Status</th>
                                                                                    {opCan(OP.UPDATE_VOYAGE) && <th>Actions</th>}
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {[...(voyage.legs ?? [])].sort((a, b) => a.sequenceNumber - b.sequenceNumber).map(leg => (
                                                                                    <tr key={leg.id}>
                                                                                        <td><span className="badge bg-secondary">L{leg.sequenceNumber}</span></td>
                                                                                        <td>{leg.departurePort}</td>
                                                                                        <td>{leg.arrivalPort ?? <span className="text-muted">—</span>}</td>
                                                                                        <td>{leg.legStartDate ? leg.legStartDate.replace('T', ' ').slice(0, 16) : '—'}</td>
                                                                                        <td>{leg.legEndDate ? leg.legEndDate.replace('T', ' ').slice(0, 16) : '—'}</td>
                                                                                        <td>
                                                                                            <span className={`badge ${leg.active ? 'bg-success' : leg.status === 'completed' ? 'bg-secondary' : 'bg-light text-dark'}`}>
                                                                                                {leg.active ? 'Active' : leg.status ?? 'Created'}
                                                                                            </span>
                                                                                        </td>
                                                                                        {opCan(OP.UPDATE_VOYAGE) && (
                                                                                            <td>
                                                                                                <div className="d-flex gap-1">
                                                                                                    <button
                                                                                                        className="btn btn-xs btn-light-primary"
                                                                                                        onClick={() => { setEditLegVoyageId(voyage.id); setEditLegTarget(leg) }}
                                                                                                        title="Edit leg"
                                                                                                    >Edit</button>
                                                                                                    {!leg.active && leg.status !== 'completed' && (
                                                                                                        <button
                                                                                                            className="btn btn-xs btn-light-success"
                                                                                                            onClick={async () => { try { await startLeg(voyage.id, leg.id); fetchVoyages(); toast.success('Leg started') } catch (e: any) { toast.error(getApiErrorMessage(e, 'Failed to start leg')) } }}
                                                                                                            title="Start leg"
                                                                                                        >Start</button>
                                                                                                    )}
                                                                                                    {leg.active && (
                                                                                                        <button
                                                                                                            className="btn btn-xs btn-light-danger"
                                                                                                            onClick={async () => { try { await endLeg(voyage.id, leg.id); fetchVoyages(); toast.success('Leg ended') } catch (e: any) { toast.error(getApiErrorMessage(e, 'Failed to end leg')) } }}
                                                                                                            title="End leg"
                                                                                                        >End</button>
                                                                                                    )}
                                                                                                </div>
                                                                                            </td>
                                                                                        )}
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </Fragment>
                                                ))
                                            )}
                                        </tbody>
                                    </table>

                                    {/* Pagination */}
                                    <div className="pagination-wrapper d-flex justify-content-between align-items-center py-3">
                                        <div className="d-flex align-items-center">
                                            <span className="text-muted me-2">Rows per page</span>
                                            <SearchableSelect
                                                className="form-select"
                                                style={{
                                                    borderRadius: "20px",
                                                    width: "70px",
                                                    border: "1px solid #dee2e6",
                                                    fontSize: "14px",
                                                    padding: "4px 8px"
                                                }}
                                                value={rowsPerPage}
                                                onChange={handleRowsPerPageChange}
                                            >
                                                <option value={10}>10</option>
                                                <option value={20}>20</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                            </SearchableSelect>
                                        </div>

                                        <div className="d-flex align-items-center">
                                            <span className="text-muted me-3" style={{ fontSize: "14px" }}>
                                                Showing <strong>{filteredVoyages.length === 0 ? 0 : ((currentPage - 1) * rowsPerPage) + 1}-{Math.min(currentPage * rowsPerPage, filteredVoyages.length)}</strong> of <strong>{filteredVoyages.length}</strong>
                                            </span>

                                            <nav>
                                                <ul className="pagination pagination-sm mb-0" style={{ gap: "2px" }}>
                                                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                                        <button
                                                            className="page-link text-muted"
                                                            style={{
                                                                backgroundColor: "#f8f9fa",
                                                                border: "1px solid #dee2e6",
                                                                padding: "8px 12px",
                                                                fontSize: "14px",
                                                                borderRadius: "6px"
                                                            }}
                                                            onClick={() => handlePageChange(currentPage - 1)}
                                                            disabled={currentPage === 1}
                                                        >
                                                            ‹
                                                        </button>
                                                    </li>

                                                    {(() => {
                                                        const pages = [];
                                                        const showPages = 5; // Show 5 page numbers at most
                                                        let startPage = Math.max(1, currentPage - 2);
                                                        let endPage = Math.min(totalPages, startPage + showPages - 1);

                                                        // Adjust start if we're near the end
                                                        if (endPage - startPage < showPages - 1) {
                                                            startPage = Math.max(1, endPage - showPages + 1);
                                                        }

                                                        // Add first page and ellipsis if needed
                                                        if (startPage > 1) {
                                                            pages.push(
                                                                <li key={1} className="page-item">
                                                                    <button
                                                                        className="page-link text-muted"
                                                                        style={{
                                                                            backgroundColor: "#f8f9fa",
                                                                            border: "1px solid #dee2e6",
                                                                            padding: "8px 12px",
                                                                            fontSize: "14px",
                                                                            minWidth: "40px",
                                                                            borderRadius: "6px"
                                                                        }}
                                                                        onClick={() => handlePageChange(1)}
                                                                    >
                                                                        1
                                                                    </button>
                                                                </li>
                                                            );

                                                            if (startPage > 2) {
                                                                pages.push(
                                                                    <li key="ellipsis1" className="page-item disabled">
                                                                        <span className="page-link border-0 text-muted" style={{ backgroundColor: "transparent", padding: "4px 8px" }}>
                                                                            ...
                                                                        </span>
                                                                    </li>
                                                                );
                                                            }
                                                        }

                                                        // Add page numbers
                                                        for (let i = startPage; i <= endPage; i++) {
                                                            pages.push(
                                                                <li key={i} className={`page-item ${currentPage === i ? "active" : ""}`}>
                                                                    <button
                                                                        className="page-link text-muted"
                                                                        style={{
                                                                            backgroundColor: currentPage === i ? "#F4F9FF" : "transparent",
                                                                            border: "1px solid #dee2e6",
                                                                            padding: "8px 12px",
                                                                            fontSize: "14px",
                                                                            minWidth: "40px",
                                                                            borderRadius: "6px",
                                                                            outline: "none",
                                                                            boxShadow: "none"
                                                                        }}
                                                                        onClick={() => handlePageChange(i)}
                                                                    >
                                                                        {i}
                                                                    </button>
                                                                </li>
                                                            );
                                                        }

                                                        // Add ellipsis and last page if needed
                                                        if (endPage < totalPages) {
                                                            if (endPage < totalPages - 1) {
                                                                pages.push(
                                                                    <li key="ellipsis2" className="page-item disabled">
                                                                        <span className="page-link border-0 text-muted" style={{ backgroundColor: "transparent", padding: "4px 8px" }}>
                                                                            ...
                                                                        </span>
                                                                    </li>
                                                                );
                                                            }

                                                            pages.push(
                                                                <li key={totalPages} className="page-item">
                                                                    <button
                                                                        className="page-link text-muted"
                                                                        style={{
                                                                            backgroundColor: "#f8f9fa",
                                                                            border: "1px solid #dee2e6",
                                                                            padding: "8px 12px",
                                                                            fontSize: "14px",
                                                                            minWidth: "40px",
                                                                            borderRadius: "6px"
                                                                        }}
                                                                        onClick={() => handlePageChange(totalPages)}
                                                                    >
                                                                        {totalPages}
                                                                    </button>
                                                                </li>
                                                            );
                                                        }

                                                        return pages;
                                                    })()}

                                                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                                        <button
                                                            className="page-link text-muted"
                                                            style={{
                                                                backgroundColor: "#f8f9fa",
                                                                border: "1px solid #dee2e6",
                                                                padding: "8px 12px",
                                                                fontSize: "14px",
                                                                borderRadius: "6px"
                                                            }}
                                                            onClick={() => handlePageChange(currentPage + 1)}
                                                            disabled={currentPage === totalPages}
                                                        >
                                                            ›
                                                        </button>
                                                    </li>
                                                </ul>
                                            </nav>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Modals */}
            {isAddVoyageModalOpen && (
                <AddVoyageModal isOpen onClose={() => setIsAddVoyageModalOpen(false)} onVoyageAdded={fetchVoyages} />
            )}
            {isDeleteVoyageModalOpen && selectedVoyage && (
                <DeleteVoyageModal
                    isOpen
                    onClose={() => setIsDeleteVoyageModalOpen(false)}
                    onVoyageDeleted={fetchVoyages}
                    voyageData={selectedVoyage}
                />
            )}
            {isEditVoyageModalOpen && selectedVoyage && (
                <EditVoyageModal
                    isOpen
                    onClose={() => setIsEditVoyageModalOpen(false)}
                    onVoyageUpdated={fetchVoyages}
                    voyageData={selectedVoyage}
                />
            )}
            {isStartEndVoyageModalOpen && selectedVoyage && (
                <StartEndVoyageModal
                    isOpen
                    onClose={() => setIsStartEndVoyageModalOpen(false)}
                    voyage={selectedVoyage}
                    onVoyageStatusUpdated={handleVoyageStatusUpdated}
                />
            )}

            {/* Leg modals */}
            {addLegVoyage && (
                <AddLegModal
                    isOpen
                    onClose={() => setAddLegVoyage(null)}
                    onLegAdded={handleLegAdded}
                    voyage={addLegVoyage}
                />
            )}
            {editLegVoyageId != null && editLegTarget != null && (
                <EditLegModal
                    isOpen
                    onClose={() => { setEditLegVoyageId(null); setEditLegTarget(null) }}
                    onLegUpdated={handleLegUpdated}
                    voyageId={editLegVoyageId}
                    leg={editLegTarget}
                />
            )}

            <ToastContainer
                position='top-center'
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                style={{ top: '5rem' }}
            />
        </div>
    )
}

export { VoyageList }
